import { Modal } from '@/components';
import type { Site, User } from '@/types/site';
import { useState } from 'react';
import {
  FacilityDetailTab,
  InvestmentDetailTab,
  LocationDetailTab,
  OverviewTab,
  PipelineDetailTab,
  WaterSupplyDetailTab,
} from './detail-tabs';

interface SiteDetailModalProps {
  isOpen: boolean;
  site: Site | null;
  managers: User[];
  onClose: () => void;
}

type TabType =
  | 'overview'
  | 'location'
  | 'water'
  | 'facility'
  | 'pipeline'
  | 'investment';

export default function SiteDetailModal({
  isOpen,
  site,
  managers,
  onClose,
}: Readonly<SiteDetailModalProps>) {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [selectedPosition, setSelectedPosition] = useState<number | null>(null);
  const [expandedMaps, setExpandedMaps] = useState<{ [key: string]: boolean }>({
    well: true,
  });

  const toggleMap = (key: string) => {
    setExpandedMaps((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!site) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="현장 상세 정보"
      size="4xl"
      maxHeight="max-h-[95vh]"
      closeOnBackdropClick
    >
      <div className="p-6">
        {/* 탭 네비게이션 */}
        <div className="flex flex-wrap border-b border-gray-200 mb-6">
          {[
            { id: 'overview', label: '전체 개요' },
            { id: 'location', label: '위치정보' },
            { id: 'water', label: '급수정보' },
            // { id: 'facility', label: '시설정보' },
            { id: 'pipeline', label: '배관정보' },
            // { id: 'investment', label: '투자정보' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
                activeTab === tab.id
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* 전체 개요 탭 */}
        {activeTab === 'overview' && (
          <OverviewTab
            site={site}
            managers={managers}
            expandedMaps={expandedMaps}
            selectedPosition={selectedPosition}
            toggleMap={toggleMap}
            setSelectedPosition={setSelectedPosition}
          />
        )}

        {/* 위치정보 탭 */}
        {activeTab === 'location' && (
          <LocationDetailTab
            site={site}
            expandedMaps={expandedMaps}
            toggleMap={toggleMap}
          />
        )}

        {/* 급수정보 탭 */}
        {activeTab === 'water' && <WaterSupplyDetailTab site={site} />}

        {/* 시설정보 탭 */}
        {activeTab === 'facility' && <FacilityDetailTab site={site} />}

        {/* 배관정보 탭 */}
        {activeTab === 'pipeline' && <PipelineDetailTab site={site} />}

        {/* 투자정보 탭 */}
        {activeTab === 'investment' && <InvestmentDetailTab site={site} />}

        {/* 하단 버튼 */}
        <div className="flex justify-end pt-6">
          <button
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg cursor-pointer whitespace-nowrap"
          >
            닫기
          </button>
        </div>
      </div>
    </Modal>
  );
}
